import { BillingInterval, PlanType } from "sdk/types";

export interface LocationState {
  extend?: boolean;
  billingInterval?: BillingInterval;
  resubscribe?: boolean;
  type: PlanType;
  upgrade?: boolean;
}

export const leaves = {
  about: "/about" as const,
  activate: "/activate" as const,
  allSet: "/all-set" as const,
  apply: "/apply" as const,
  author: "/author" as const,
  cancellationReason: "/cancellation-reason" as const,
  careers: "/careers" as const,
  clinics: "/clinics" as const,
  confirmation: "/confirmation" as const,
  crew: "/crew" as const,
  dashboard: "/dashboard" as const,
  diagnosticsRoot: "/diagnostics" as const,
  diagnosticsConfirmation: "/confirmation" as const,
  diagnosticsDashboard: "/dashboard" as const,
  diagnosticsPatientForm: "/patient-form" as const,
  diagnosticsIntake: "/products/:diagnostic_user_product_id/intake" as const,
  diagnosticsIntakeComplete: "/consultations/intake_completed" as const,
  diagnosticsSignIn: "/sign-in" as const,
  events: "/events" as const,
  faqs: "/faqs" as const,
  forgotPassword: "/forgot-password" as const,
  howItWorks: "/how-it-works" as const,
  interstitialThanksForSharing: "/thanks-for-sharing" as const,
  interstitialLogistics: "/e5ce59" as const, // /logistics
  interstitialWelcome: "/cece11" as const, // /welcome
  library: "/library" as const,
  manageBilling: "/manage-billing" as const,
  managePlan: "/manage-plan" as const,
  medicalReviewer: "/medical-reviewer" as const,
  memberRoot: "/member" as const,
  membershipPayment: "/membership-payment" as const,
  membershipSelect: "/membership-select" as const,
  onboarding: "/onboarding" as const,
  onboardingRoot: "/onboarding" as const,
  onDemandCoaching: "/on-demand-coaching" as const,
  ourApproach: "/our-approach" as const,
  partnerRoot: "/partner" as const,
  partnerSlugRoot: "/partner/:slug" as const,
  partnerOnboardingRoot: "/partner/:slug/onboarding" as const,
  paymentIssue: "/payment-issue" as const,
  philosophy: "/philosophy" as const,
  planDetailsRoot: "/plan" as const,
  planPayment: "/payment" as const,
  planSelect: "/select" as const,
  pricingChart: "/how-it-works#plans" as const,
  program: "/program" as const,
  questionBirthday: "/8eaf2a" as const, // /birthday
  questionCoverage: "/d251e4" as const, // /coverage
  questionClinicRefer: "/4ccde8" as const, // /clinic-refer
  questionClinicSatisfaction: "/4ccdee" as const, // /clinic-satisfaction
  questionCurrentClinic: "/4ccdf0" as const, // /current-clinic
  questionDueDate: "/z1b3a4" as const, // /due-date
  questionEmail: "/b930de" as const, // /email
  questionEmployer: "/d0c752" as const, // /employer
  questionHealthConditions: "/3592c1" as const, // /health-conditions
  questionHowAnxious: "/64b058" as const, // /how-anxious
  questionHowDidYouHear: "/db39f1" as const, // /how-did-you-hear
  questionHowGrateful: "/3f4f2e" as const, // /how-grateful
  questionHowLong: "/4dbb0e" as const, // /how-long
  questionHowOptimistic: "/e16396" as const, // /how-optimistic
  questionHowSupported: "/5c4ab9" as const, // /how-supported
  questionJourneyLooksLike: "/850c26" as const, // /journey-looks-like
  questionNextMilestone: "/7c2k21" as const, // /next-milestone
  questionMedicalDiagnosis: "/42b699" as const, // /medical-diagnosis
  questionMostChallenging: "/cc5666" as const, // /most-challenging
  questionMoreTreatmentDetails: "/8f1c3d" as const, // /more-treatment-details
  questionName: "/24fb80" as const, // /name
  // Retired: the partner question was replaced by more-treatment-details. Kept
  // only as a legacy redirect target so existing /onboarding/partner URLs resolve.
  questionPartner: "/73fc66" as const, // /partner
  questionPhone: "/ee1282" as const, // /phone
  questionPregnancyBackground: "/b43014" as const, // /pregnancy-background
  questionProvidersSeen: "/c2e66d" as const, // /providers-seen
  questionProviderSelect: "/c2e67f" as const, // /provider-select
  questionSupportNeeds: "/s3e77d" as const, // /support-needs
  questionZip: "/644fb6" as const, // /zip
  referAFriend: "/refer-a-friend" as const,
  resetPassword: "/reset-password" as const,
  root: "/" as const,
  setPassword: "/set-password" as const,
  settings: "/settings" as const,
  settingsRoot: "/settings" as const,
  signIn: "/login" as const,
  slug: "/:slug" as const,
  topic: "/:topic" as const,
  tryingToConceive: "/trying-to-conceive" as const,
};

const onboarding = leaves.onboardingRoot;

// IMPORTANT: This list must be kept in sync with the list of routes in
// config/routes.rb
export const routes = {
  about: leaves.about,
  allSet: `${leaves.memberRoot}${leaves.allSet}` as const,
  article: `${leaves.library}${leaves.topic}${leaves.slug}` as const,
  author: `${leaves.author}${leaves.slug}` as const,
  cancellationReason: `${leaves.memberRoot}${leaves.cancellationReason}`,
  careers: leaves.careers,
  clinics: leaves.clinics,
  confirmation: `${leaves.memberRoot}${leaves.confirmation}` as const,
  crew: `${leaves.memberRoot}${leaves.crew}` as const,
  dashboard: `${leaves.memberRoot}${leaves.dashboard}` as const,
  diagnosticsRoot: leaves.diagnosticsRoot,
  diagnosticsPatientForm:
    `${leaves.diagnosticsRoot}${leaves.diagnosticsPatientForm}` as const,
  diagnosticsConfirmation:
    `${leaves.diagnosticsRoot}${leaves.diagnosticsConfirmation}` as const,
  diagnosticsDashboard:
    `${leaves.diagnosticsRoot}${leaves.diagnosticsDashboard}` as const,
  diagnosticsIntake:
    `${leaves.diagnosticsRoot}${leaves.diagnosticsIntake}` as const,
  diagnosticsIntakeComplete:
    `${leaves.diagnosticsRoot}${leaves.diagnosticsIntakeComplete}` as const,
  diagnosticsSignIn:
    `${leaves.diagnosticsRoot}${leaves.diagnosticsSignIn}` as const,
  faqs: leaves.faqs,
  forgotPassword: leaves.forgotPassword,
  gathering: `${leaves.events}${leaves.slug}` as const,
  gatherings: leaves.events,
  howItWorks: leaves.howItWorks,
  interstitialThanksForSharing:
    `${onboarding}${leaves.interstitialThanksForSharing}` as const,
  interstitialLogistics:
    `${onboarding}${leaves.interstitialLogistics}` as const,
  interstitialWelcome: `${onboarding}${leaves.interstitialWelcome}` as const,
  library: leaves.library,
  medicalReviewer: `${leaves.medicalReviewer}${leaves.slug}` as const,
  onboardingRoot: leaves.onboardingRoot,
  ourApproach: leaves.ourApproach,
  onDemandCoaching: leaves.onDemandCoaching,
  partnerRoot: leaves.partnerRoot,
  partnerSlugRoot: leaves.partnerSlugRoot,
  partnerOnboardingRoot: leaves.partnerOnboardingRoot,
  partnerOnboardingQuestionName:
    `${leaves.partnerOnboardingRoot}${leaves.questionName}` as const,
  partnerOnboardingQuestionEmail:
    `${leaves.partnerOnboardingRoot}${leaves.questionEmail}` as const,
  partnerOnboardingQuestionHowLong:
    `${leaves.partnerOnboardingRoot}${leaves.questionHowLong}` as const,
  partnerOnboardingQuestionDueDate:
    `${leaves.partnerOnboardingRoot}${leaves.questionDueDate}` as const,
  partnerOnboardingQuestionProvidersSeen:
    `${leaves.partnerOnboardingRoot}${leaves.questionProvidersSeen}` as const,
  partnerOnboardingQuestionProviderSelect:
    `${leaves.partnerOnboardingRoot}${leaves.questionProviderSelect}` as const,
  partnerOnboardingQuestionJourneyLooksLike:
    `${leaves.partnerOnboardingRoot}${leaves.questionJourneyLooksLike}` as const,
  partnerOnboardingQuestionNextMilestone:
    `${leaves.partnerOnboardingRoot}${leaves.questionNextMilestone}` as const,
  partnerOnboardingQuestionMostChallenging:
    `${leaves.partnerOnboardingRoot}${leaves.questionMostChallenging}` as const,
  partnerOnboardingQuestionClinicSatisfaction:
    `${leaves.partnerOnboardingRoot}${leaves.questionClinicSatisfaction}` as const,
  partnerOnboardingQuestionSupportNeeds:
    `${leaves.partnerOnboardingRoot}${leaves.questionSupportNeeds}` as const,
  partnerOnboardingQuestionMoreTreatmentDetails:
    `${leaves.partnerOnboardingRoot}${leaves.questionMoreTreatmentDetails}` as const,
  partnerOnboardingQuestionBirthday:
    `${leaves.partnerOnboardingRoot}${leaves.questionBirthday}` as const,
  partnerOnboardingQuestionPhone:
    `${leaves.partnerOnboardingRoot}${leaves.questionPhone}` as const,
  partnerOnboardingInterstitialThanksForSharing:
    `${leaves.partnerOnboardingRoot}${leaves.interstitialThanksForSharing}` as const,
  partnerActivate: `${leaves.partnerSlugRoot}${leaves.activate}` as const,
  paymentIssue: `${leaves.memberRoot}${leaves.paymentIssue}` as const,
  planPayment: `${leaves.planDetailsRoot}${leaves.planPayment}` as const,
  planSelect: `${leaves.planDetailsRoot}${leaves.planSelect}` as const,
  program: `${leaves.memberRoot}${leaves.program}` as const,
  questionBirthday:
    `${leaves.onboardingRoot}${leaves.questionBirthday}` as const,
  questionCoverage: `${onboarding}${leaves.questionCoverage}` as const,
  questionClinicRefer: `${onboarding}${leaves.questionClinicRefer}` as const,
  questionClinicSatisfaction:
    `${onboarding}${leaves.questionClinicSatisfaction}` as const,
  questionCurrentClinic:
    `${onboarding}${leaves.questionCurrentClinic}` as const,

  questionDueDate: `${onboarding}${leaves.questionDueDate}` as const,
  questionEmail: `${leaves.onboardingRoot}${leaves.questionEmail}` as const,
  questionEmployer: `${onboarding}${leaves.questionEmployer}` as const,
  questionHealthConditions:
    `${onboarding}${leaves.questionHealthConditions}` as const,
  questionHowAnxious: `${onboarding}${leaves.questionHowAnxious}` as const,
  questionHowDidYouHear:
    `${onboarding}${leaves.questionHowDidYouHear}` as const,
  questionHowGrateful: `${onboarding}${leaves.questionHowGrateful}` as const,
  questionHowLong: `${onboarding}${leaves.questionHowLong}` as const,
  questionHowOptimistic:
    `${onboarding}${leaves.questionHowOptimistic}` as const,
  questionHowSupported: `${onboarding}${leaves.questionHowSupported}` as const,
  questionJourneyLooksLike:
    `${onboarding}${leaves.questionJourneyLooksLike}` as const,
  questionNextMilestone:
    `${onboarding}${leaves.questionNextMilestone}` as const,
  questionMedicalDiagnosis:
    `${onboarding}${leaves.questionMedicalDiagnosis}` as const,
  questionMostChallenging:
    `${onboarding}${leaves.questionMostChallenging}` as const,
  questionMoreTreatmentDetails:
    `${onboarding}${leaves.questionMoreTreatmentDetails}` as const,
  questionName: `${onboarding}${leaves.questionName}` as const,
  questionPhone: `${onboarding}${leaves.questionPhone}` as const,
  questionPregnancyBackground:
    `${onboarding}${leaves.questionPregnancyBackground}` as const,
  questionProvidersSeen:
    `${onboarding}${leaves.questionProvidersSeen}` as const,
  questionSupportNeeds: `${onboarding}${leaves.questionSupportNeeds}` as const,
  questionZip: `${onboarding}${leaves.questionZip}` as const,
  referAFriend: `${leaves.memberRoot}${leaves.referAFriend}` as const,
  resetPassword: leaves.resetPassword,
  root: leaves.root,
  setPassword: `${leaves.memberRoot}${leaves.setPassword}` as const,
  settings: `${leaves.memberRoot}${leaves.settings}` as const,
  managePlan: `${leaves.memberRoot}${leaves.managePlan}` as const,
  manageBilling: `${leaves.memberRoot}${leaves.manageBilling}` as const,
  signIn: leaves.signIn,
  signUp: leaves.onboardingRoot,
  topic: `${leaves.library}${leaves.topic}` as const,
};
export type RoutesKeys = keyof typeof routes;
export type RoutesValues = (typeof routes)[RoutesKeys];

export const landingAppRoutes = [
  routes.root,
  routes.about,
  routes.article,
  routes.careers,
  routes.clinics,
  routes.faqs,
  routes.howItWorks,
  routes.library,
  routes.gathering,
  routes.gatherings,
];

export const mixedRoutes = [routes.signIn, routes.signUp];

export const chatGuidelinesDocUrl =
  "https://docs.google.com/document/d/1YIYcgQpo0OZG7NKzskCg5le5imaVTkJVClpe07Lpn4U";

export const nurseLinkTreeUrl = "https://conceive.as.me/fertilitynurses";

export const coachLinkTreeUrl = "https://conceive.as.me/coaches";
